import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { DeviceLanguageList, DeviceLanguageSignals } from './types/deviceLanguage';
import { EgeCountryTotal, EgeDetailPage, EgeHourlyCell, EgeHourlyPoint, EgeMonthlyUsers, EgeStatsFilters, EgeStatsOptions, EgeTimeseriesPoint } from './types/egeStats';
import { MetaSearchRequest, MetaSearchResult, MetricsSaveRequest } from './types/service';
import { sec } from './security';
import {
  AssignUsersToCampaignRequest,
  Audience,
  AudienceBookmark,
  AudienceRelationsRequest,
  AudienceRequest,
  CheckAdvertiserPresenceRequest,
  CheckAdvertiserPresenceResponse,
  CreateCampaignRequest,
  EntityFlightAircraftSearchRequest,
  EntityFlightAircraftSearchResponse,
  EntityFlightJourneyRequest,
  EntityFlightJourneyResponse,
  EntityFlightPassengersRequest,
  EntityFlightPassengersResponse,
  EntityFlightSearchRequest,
  EntityFlightStatusResponse,
  EntityFlightSummary,
  EntityNote,
  EntityVesselJourneyRequest,
  EntityVesselJourneyResponse,
  EntityVesselSearchRequest,
  EntityVesselStatusResponse,
  EntityVesselSummary,
  GADMCountry,
  GeonamesResult,
  GetCampaignEntityTimelineResponse,
  GetCampaignResponse,
  GetCampaignsListRequest,
  GetCampaignsListResponse,
  GetCampaignTimelineRequest,
  GetCampaignTimelineResponse,
  GetEntityDwellForLocationsRequest,
  GetEntityDwellForLocationsResponse,
  GetEntityScoresRequest,
  GetEntityScoresResponse,
  GetLocationNetworkResponse,
  GetLocationsRequest,
  GetLocationsResponse,
  GetLocationsResponseWithMetadata,
  GetLocationUnitsResponse,
  GetTackleboxHistoryPageResponse,
  TackleboxJobShare,
  LocationBookmark,
  LocationDailyVisitsResponse,
  LocationVisitorsDailyRequest,
  PermissionsUser,
  PolygonNote,
  SaveAudienceBookmarkRequest,
  SavedState,
  SavedView,
  SaveEntityNoteRequest,
  SaveLocationBookmarkRequest,
  SaveLocationNoteRequest,
  SaveStateRequest,
  TitoRequest,
  User,
  UserNotification,
  View,
  WifiHexRequest,
  WifiHexResponseRow
} from './types';
import { TackleboxJobRespone, TouchstoneStatsByCategory } from './types/tacklebox';

const { VITE_SERVICE_HOST } = import.meta.env;

// Define a service using a base URL and expected endpoints
/**
 * The query string every filtered EGE endpoint takes.
 *
 * Empty `sources` / `countries` are OMITTED rather than sent empty: the service reads an
 * absent filter as "all", and sending `sources=` would be an empty list, which means
 * nothing at all. The two readings differ by an entire page of data.
 */
const egeStatsParams = (filters: EgeStatsFilters) => ({
  start_date: filters.startDate,
  end_date: filters.endDate,
  date_field: filters.dateField,
  ...(filters.sources.length ? { sources: filters.sources.join(',') } : {}),
  ...(filters.countries.length ? { countries: filters.countries.join(',') } : {})
});

export const service = createApi({
  tagTypes: [
    //n
    'state_hashes',
    'location_note',
    'entity_note',
    'user_notification',
    'location_bookmarks',
    'audience_bookmarks',
    'tacklebox_history',
    'tacklebox_shares',
    'campaigns',
    'campaign_users',
    'saved_views',
    'location_unit_info',
    'locations'
  ],
  reducerPath: 'service',
  baseQuery: fetchBaseQuery({
    baseUrl: VITE_SERVICE_HOST,
    prepareHeaders: async (headers) => {
      const access_token = await sec.getAccessTokenSilently()();
      if (access_token) headers.set('Authorization', `Bearer ${access_token}`);
      return headers;
    }
  }),
  endpoints: (builder) => ({
    submitAuthError: builder.mutation<void, { error_message: string; session_id: string }>({
      query: ({ error_message, session_id }) => ({
        method: 'POST',
        url: '/v0/auth/log-error',
        body: { error_message, session_id }
      })
    }),
    saveMetric: builder.mutation<void, MetricsSaveRequest>({
      query: (body) => ({
        method: 'POST',
        url: '/v0/metrics',
        body
      })
    }),
    approveUserForCampaign: builder.mutation<void, { user_id: string; campaign_id: string }>({
      invalidatesTags: ['campaign_users'],
      query: ({ user_id, campaign_id }) => ({
        method: 'PUT',
        url: `/v0/campaign/${campaign_id}/approve/${user_id}`
      })
    }),
    updateUserRoleForCampaign: builder.mutation<void, { user_id: string; campaign_id: string; role: string }>({
      invalidatesTags: ['campaign_users'],
      query: ({ user_id, campaign_id, role }) => ({
        method: 'PUT',
        url: `/v0/campaign/${campaign_id}/user/${user_id}/${role}`
      })
    }),
    removeUserFromCampaign: builder.mutation<void, { user_id: string; campaign_id: string }>({
      invalidatesTags: ['campaign_users'],
      query: ({ user_id, campaign_id }) => ({
        method: 'DELETE',
        url: `/v0/campaign/${campaign_id}/user/${user_id}`
      })
    }),
    getCampaignShareSuggestions: builder.query<string[], void>({
      query: () => ({
        method: 'GET',
        url: `/v0/campaign/suggested-users`
      })
    }),
    getUsersForCampaign: builder.query<PermissionsUser[], string>({
      providesTags: ['campaign_users'],
      query: (campaign_id) => ({
        method: 'GET',
        url: `/v0/campaign/${campaign_id}/users`
      })
    }),
    addUsersToCampaign: builder.mutation<void, AssignUsersToCampaignRequest>({
      invalidatesTags: ['campaign_users'],
      query: ({ user, campaign_id }) => ({
        method: 'POST',
        url: `/v0/campaign/${campaign_id}/assign-users`,
        body: user
      })
    }),
    getUserSuggestions: builder.query<string[], { term: string; campaign_id?: string }>({
      query: ({ term, campaign_id }) => ({
        url: '/v0/user/user-suggestions',
        method: 'POST',
        body: {
          term,
          campaign_id
        }
      })
    }),
    getContextualSearchResults: builder.query<MetaSearchResult[], MetaSearchRequest>({
      query: ({ term, campaign_id, bounds, categories }) => {
        const url = `/v0/meta/search/${term}` + (campaign_id ? `/${campaign_id}` : '');
        const params: Record<string, string> = {};
        if (bounds) {
          params.bounds = bounds.join(',');
        }
        if (categories && categories.length > 0) {
          params.categories = categories.join(',');
        }
        return {
          url,
          method: 'GET',
          params
        };
      }
    }),
    getBoundsForEntities: builder.mutation<void, string[]>({
      query: (entity_ids) => ({
        method: 'POST',
        url: '/v0/audience/bounds',
        body: {
          entity_ids
        }
      })
    }),
    getUser: builder.query<User, void>({
      query: () => '/v0/user'
    }),
    saveState: builder.mutation<{ hash: string }, SaveStateRequest>({
      invalidatesTags: ['state_hashes'],
      query: (body: SaveStateRequest) => ({
        method: 'POST',
        url: '/v0/state/save',
        body
      })
    }),
    updateSavedState: builder.mutation<void, { hash: string; name: string }>({
      invalidatesTags: ['state_hashes'],
      query: ({ hash, name }) => ({
        method: 'PUT',
        url: `/v0/state/${hash}`,
        body: { name }
      })
    }),
    deleteSavedState: builder.mutation<void, string>({
      invalidatesTags: ['state_hashes'],
      query: (hash) => ({
        method: 'DELETE',
        url: `/v0/state/${hash}`
      })
    }),
    getSavedStates: builder.query<SavedState[], void>({
      providesTags: ['state_hashes'],
      query: () => ({
        method: 'GET',
        url: `/v0/state`
      })
    }),
    // creating some cyclical dependencies here.. using `any` here to avoid it
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getSavedState: builder.query<any, string>({
      query: (hash) => ({
        method: 'GET',
        url: `/v0/state/fetch/${hash}`
      })
    }),
    getLocationNotes: builder.query<PolygonNote[], string>({
      providesTags: ['location_note'],
      query: (polygon_id) => ({
        method: 'GET',
        url: `/v0/location/notes/${polygon_id}`
      })
    }),
    getLocationNetwork: builder.query<GetLocationNetworkResponse[], string>({
      query: (polygon_id) => ({
        method: 'GET',
        url: `/v0/location/network/${polygon_id}`
      })
    }),
    saveLocationNote: builder.mutation<void, SaveLocationNoteRequest>({
      invalidatesTags: ['location_note'],
      query: ({ note, polygon_id }) => ({
        method: 'POST',
        url: '/v0/location/note',
        params: { polygon_id, note }
      })
    }),
    deleteLocationNote: builder.mutation<void, string>({
      invalidatesTags: ['location_note'],
      query: (note_id) => ({
        method: 'DELETE',
        url: `/v0/location/note/${note_id}`
      })
    }),
    getEntityNotes: builder.query<EntityNote[], string>({
      providesTags: ['entity_note'],
      query: (entity_id) => ({
        method: 'GET',
        url: `/v0/audience/notes/${entity_id}`
      })
    }),
    saveEntityNote: builder.mutation<void, SaveEntityNoteRequest>({
      invalidatesTags: ['entity_note'],
      query: ({ note, entity_id }) => ({
        method: 'POST',
        url: '/v0/audience/note',
        params: { entity_id, note }
      })
    }),
    deleteEntityNote: builder.mutation<void, string>({
      invalidatesTags: ['entity_note'],
      query: (note_id) => ({
        method: 'DELETE',
        url: `/v0/audience/note/${note_id}`
      })
    }),
    getNotifications: builder.query<UserNotification[], void>({
      providesTags: ['user_notification'],
      query: () => ({
        method: 'GET',
        url: `/v0/user/notifications`
      })
    }),
    deleteNotification: builder.mutation<void, string>({
      invalidatesTags: ['user_notification'],
      query: (notification_id) => ({
        method: 'DELETE',
        url: `/v0/user/notification/${notification_id}`
      })
    }),
    deleteAllNotifications: builder.mutation<void, void>({
      invalidatesTags: ['user_notification'],
      query: () => ({
        method: 'DELETE',
        url: `/v0/user/notifications`
      })
    }),
    seenNotification: builder.mutation<void, string>({
      invalidatesTags: ['user_notification'],
      query: (id: string) => ({
        method: 'PUT',
        url: `/v0/user/notification/${id}/seen`
      })
    }),
    seenAllNotifications: builder.mutation<void, void>({
      invalidatesTags: ['user_notification'],
      query: () => ({
        method: 'PUT',
        url: `/v0/user/notifications/seen`
      })
    }),
    getLocations: builder.query<GetLocationsResponseWithMetadata, GetLocationsRequest>({
      query: ({ advertiser_ids, bounding_box, countries, polygon_ids, sort, order, categories, min_unique_entities, name, campaign, mapZoom }) => ({
        method: 'POST',
        url: '/v0/location' + (campaign ? `/campaign/${campaign.id}` : ''),
        body: {
          advertiser_ids,
          bounding_box,
          countries: countries && countries?.length > 0 ? countries.map((d) => d.iso3) : undefined,
          polygon_ids,
          categories: categories && categories.length > 0 ? categories : undefined,
          sort,
          order,
          min_unique_entities,
          name,
          sampleOK: mapZoom !== undefined && mapZoom < 4
        }
      }),
      providesTags: ['locations']
    }),
    getLocationVisitorsDaily: builder.query<LocationDailyVisitsResponse[], LocationVisitorsDailyRequest>({
      query: ({ polygon_id, date_range }) => ({
        url: `/v0/location/daily-visits`,
        method: 'POST',
        body: {
          date_range,
          polygon_id
        }
      })
    }),
    getAudience: builder.query<Audience[], AudienceRequest>({
      query: ({ polygon_ids, audience_ids, min_polygon_interaction, sort, order, min_score, campaign }) => ({
        method: 'POST',
        url: '/v0/audience' + (campaign ? `/campaign/${campaign.id}` : ''),
        body: {
          polygon_ids: polygon_ids && polygon_ids.length > 0 ? polygon_ids : undefined,
          audience_ids: audience_ids && audience_ids.length > 0 ? audience_ids : undefined,
          min_polygon_interaction: (min_polygon_interaction ?? 0) > 0 ? min_polygon_interaction : undefined,
          min_score,
          sort,
          order
        }
      })
    }),
    getEntityFlightStatus: builder.query<EntityFlightStatusResponse, void>({
      query: () => ({
        method: 'GET',
        url: '/v0/entity-flights/status'
      })
    }),
    getEntityFlightSummaries: builder.query<EntityFlightSummary[], EntityFlightSearchRequest>({
      query: (body) => ({
        method: 'POST',
        url: '/v0/entity-flights/search',
        body
      })
    }),
    getEntityFlightAircraftSummaries: builder.query<EntityFlightAircraftSearchResponse, EntityFlightAircraftSearchRequest>({
      query: (body) => ({
        method: 'POST',
        url: '/v0/entity-flights/aircraft/search',
        body
      })
    }),
    getEntityFlightPassengers: builder.query<EntityFlightPassengersResponse, EntityFlightPassengersRequest>({
      query: (body) => ({
        method: 'POST',
        url: '/v0/entity-flights/aircraft/passengers',
        body
      })
    }),
    getEntityFlightJourneys: builder.query<EntityFlightJourneyResponse, EntityFlightJourneyRequest>({
      query: ({ advertiser_id, start_at, end_at, evidence_types, sources, limit, cursor }) => ({
        method: 'GET',
        url: `/v0/entity-flights/${encodeURIComponent(advertiser_id)}`,
        params: {
          start_at,
          end_at,
          evidence_types: evidence_types?.join(','),
          sources: sources?.join(','),
          limit,
          cursor
        }
      })
    }),
    getEntityVesselStatus: builder.query<EntityVesselStatusResponse, void>({
      query: () => ({
        method: 'GET',
        url: '/v0/entity-vessels/status'
      })
    }),
    getEntityVesselSummaries: builder.query<EntityVesselSummary[], EntityVesselSearchRequest>({
      query: (body) => ({
        method: 'POST',
        url: '/v0/entity-vessels/search',
        body
      })
    }),
    getEntityVesselJourneys: builder.query<EntityVesselJourneyResponse, EntityVesselJourneyRequest>({
      query: ({ advertiser_id, start_at, end_at, evidence_types, sources, limit, cursor }) => ({
        method: 'GET',
        url: `/v0/entity-vessels/${encodeURIComponent(advertiser_id)}`,
        params: {
          start_at,
          end_at,
          evidence_types: evidence_types?.join(','),
          sources: sources?.join(','),
          limit,
          cursor
        }
      })
    }),
    getAudienceRelations: builder.query<{ entities: Audience[]; polygons: GetLocationsResponse[] }, AudienceRelationsRequest>({
      query: ({ entities, campaign }) => ({
        method: 'POST',
        url: `/v0/audience/campaign/${campaign?.id || 'null'}/entities`,
        body: entities
      })
    }),
    getLocation: builder.mutation<GetLocationsResponseWithMetadata, string>({
      query: (polygon_id) => ({
        method: 'POST',
        url: `/v0/location`,
        body: {
          polygon_ids: [polygon_id]
        }
      })
    }),
    getEntity: builder.mutation<Audience, string>({
      query: (entity_id) => ({
        method: 'POST',
        url: `/v0/audience`,
        body: {
          audience_ids: [entity_id]
        }
      }),
      transformResponse: (response: Audience[]) => response[0]
    }),
    getLocationBookmarks: builder.query<LocationBookmark[], string>({
      providesTags: ['location_bookmarks'],
      query: (project_id) => ({
        method: 'GET',
        url: `/v0/location/bookmarks/${project_id}`
      })
    }),
    saveLocationBookmark: builder.mutation<void, SaveLocationBookmarkRequest>({
      invalidatesTags: ['location_bookmarks'],
      query: ({ bookmark_name, polygon_ids, project_id }) => ({
        method: 'POST',
        url: '/v0/location/bookmark',
        body: { bookmark_name, polygon_ids, project_id }
      })
    }),
    deleteLocationBookmark: builder.mutation<void, string>({
      invalidatesTags: ['location_bookmarks'],
      query: (bookmark_id) => ({
        method: 'DELETE',
        url: `/v0/location/bookmark/${bookmark_id}`
      })
    }),
    getAudienceBookmarks: builder.query<AudienceBookmark[], string>({
      providesTags: ['audience_bookmarks'],
      query: (project_id) => ({
        method: 'GET',
        url: `/v0/audience/bookmarks/${project_id}`
      })
    }),
    saveAudienceBookmark: builder.mutation<void, SaveAudienceBookmarkRequest>({
      invalidatesTags: ['audience_bookmarks'],
      query: ({ bookmark_name, advertiser_ids, project_id }) => ({
        method: 'POST',
        url: '/v0/audience/bookmark',
        body: { bookmark_name, advertiser_ids, project_id }
      })
    }),
    deleteAudienceBookmark: builder.mutation<void, string>({
      invalidatesTags: ['audience_bookmarks'],
      query: (bookmark_id) => ({
        method: 'DELETE',
        url: `/v0/audience/bookmark/${bookmark_id}`
      })
    }),
    getGADMCountries: builder.query<GADMCountry[], void>({
      query: () => ({
        method: 'GET',
        url: '/v0/meta/gadm'
      })
    }),
    getLocales: builder.query<{ code: string; label: string }[], void>({
      query: () => ({
        method: 'GET',
        url: '/v0/meta/locales'
      })
    }),
    getEntityDateRange: builder.query<[string | null, string | null], string>({
      query: (entity_id) => ({
        method: 'GET',
        url: `/v0/audience/seen/${entity_id}`
      })
    }),
    getEntityDwellForLocations: builder.query<GetEntityDwellForLocationsResponse[], GetEntityDwellForLocationsRequest>({
      query: ({ entity_id, polygon_id, date_range }) => ({
        method: 'POST',
        url: `/v0/audience/metrics`,
        body: {
          polygon_id,
          entity_id,
          date_range
        }
      })
    }),
    getEntityScores: builder.query<GetEntityScoresResponse[], GetEntityScoresRequest>({
      query: ({ entity_id, polygon_ids, campaign_id }) => ({
        method: 'POST',
        url: `/v0/audience/scores`,
        body: {
          polygon_ids,
          entity_id,
          campaign_id
        }
      })
    }),
    getCampaignTimeline: builder.query<GetCampaignTimelineResponse[], GetCampaignTimelineRequest>({
      query: ({ campaign_id, entity_ids }) => ({
        method: 'POST',
        url: `/v0/campaign/${campaign_id}/timeline`,
        body: {
          entity_ids
        }
      })
    }),
    getCampaignEntityTimeline: builder.query<GetCampaignEntityTimelineResponse[], string>({
      query: (campaign_id) => ({
        method: 'GET',
        url: `/v0/campaign/${campaign_id}/entity-timeline`
      })
    }),
    deleteCampaign: builder.mutation<void, string>({
      invalidatesTags: ['campaigns'],
      query: (campaign_id) => ({
        method: 'DELETE',
        url: `/v0/campaign/${campaign_id}`
      })
    }),
    undeleteCampaign: builder.mutation<void, string>({
      invalidatesTags: ['campaigns'],
      query: (campaign_id) => ({
        method: 'PUT',
        url: `/v0/campaign/${campaign_id}/undelete`
      })
    }),
    toggleCampaignEnabled: builder.mutation<void, string>({
      invalidatesTags: ['campaigns'],
      query: (campaign_id) => ({
        method: 'PUT',
        url: `/v0/campaign/${campaign_id}/enabled`
      })
    }),
    renameCampaign: builder.mutation<void, { campaign_id: string; name: string }>({
      invalidatesTags: ['campaigns'],
      query: ({ campaign_id, name }) => ({
        method: 'PUT',
        url: `/v0/campaign/${campaign_id}/name`,
        body: { name }
      })
    }),
    getCampaigns: builder.query<GetCampaignsListResponse, GetCampaignsListRequest>({
      providesTags: ['campaigns'],
      query: ({ page, pageSize, search, tab, includeDisabled, sortBy, sortOrder }) => ({
        method: 'GET',
        url: '/v0/campaign',
        params: {
          page,
          pageSize,
          search: search || undefined,
          tab,
          includeDisabled,
          sortBy,
          sortOrder
        }
      })
    }),
    getCampaign: builder.query<GetCampaignResponse | null, string>({
      providesTags: ['campaigns'],
      query: (id) => ({
        method: 'GET',
        url: `/v0/campaign/${id}`
      })
    }),
    createCampaign: builder.mutation<{ id: string }, CreateCampaignRequest>({
      invalidatesTags: ['campaigns'],
      query: ({ name, ifChoice, pairs, endDate, touchstoneStartDate, touchstoneEndDate, edgeStartDate, edgeEndDate, continuous, minPolygonScore }) => ({
        method: 'POST',
        url: '/v0/campaign',
        body: {
          name,
          if: {
            item: ifChoice.item,
            meta: ifChoice.meta,
            selection: ifChoice.selection
          },
          pairs: pairs.map((pair) => ({
            subject: {
              item: pair.subject.item,
              meta: pair.subject.meta,
              selection: pair.subject.selection
            },
            condition: {
              item: pair.condition.item,
              meta: pair.condition.meta,
              selection: pair.condition.selection,
              secondarySelection: pair.condition.secondarySelection
            }
          })),
          endDate,
          touchstoneStartDate,
          touchstoneEndDate,
          edgeStartDate,
          edgeEndDate,
          continuous,
          minPolygonScore
        }
      })
    }),
    titoRequest: builder.mutation<void, TitoRequest>({
      invalidatesTags: ['tacklebox_history'],
      query: ({ name, features, minPolygons }) => ({
        method: 'POST',
        url: '/v0/tacklebox/tito',
        body: {
          name,
          features,
          minPolygons
        }
      })
    }),
    getTackleboxHistory: builder.query<GetTackleboxHistoryPageResponse, { tab: 'mine' | 'shared'; page: number; pageSize: number }>({
      providesTags: ['tacklebox_history'],
      query: ({ tab, page, pageSize }) => ({
        method: 'GET',
        url: '/v0/tacklebox/history',
        params: { tab, page, pageSize }
      })
    }),
    getTackleboxJobShares: builder.query<TackleboxJobShare[], string>({
      providesTags: ['tacklebox_shares'],
      query: (jobId) => ({
        method: 'GET',
        url: `/v0/tacklebox/job/${jobId}/shares`
      })
    }),
    shareTackleboxJob: builder.mutation<void, { job_id: string; user: string; message: string }>({
      invalidatesTags: ['tacklebox_shares'],
      query: ({ job_id, user, message }) => ({
        method: 'POST',
        url: `/v0/tacklebox/job/${job_id}/share`,
        body: { user, message }
      })
    }),
    unshareTackleboxJob: builder.mutation<void, { job_id: string; user: string }>({
      invalidatesTags: ['tacklebox_shares'],
      query: ({ job_id, user }) => ({
        method: 'DELETE',
        url: `/v0/tacklebox/job/${job_id}/share/${encodeURIComponent(user)}`
      })
    }),
    deleteTackleboxHistory: builder.mutation<void, string>({
      invalidatesTags: ['tacklebox_history'],
      query: (id) => ({
        method: 'DELETE',
        url: `/v0/tacklebox/history/${id}`
      })
    }),
    renameTackleboxJob: builder.mutation<{ id: string; name: string }, { job_id: string; name: string }>({
      invalidatesTags: ['tacklebox_history'],
      query: ({ job_id, name }) => ({
        method: 'PATCH',
        url: `/v0/tacklebox/job/${job_id}`,
        body: { name }
      })
    }),
    getTackleboxJob: builder.query<TackleboxJobRespone, { job_id: string }>({
      query: ({ job_id }) => ({
        method: 'GET',
        url: `/v0/tacklebox/job/${job_id}`
      })
    }),
    getTouchstoneStats: builder.query<TouchstoneStatsByCategory, void>({
      query: () => ({
        method: 'GET',
        url: '/v0/stats/touchstone'
      })
    }),
    // --- EGE statistics -------------------------------------------------------
    //
    // `cnt` only. The service serves no distinct counts: totalling them over a date
    // range overstates by however much the population repeats, so the map colours by
    // record volume. The underlying columns exist and are exact per day/month/hour,
    // for a later feature that graphs them AT one of those grains.
    /** Languages the "uses language" picker offers: every one with data, most devices first. */
    getDeviceLanguages: builder.query<DeviceLanguageList, void>({
      query: () => ({
        method: 'GET',
        url: '/v0/stats/device-language/languages'
      })
    }),
    /**
     * How much a "uses language" condition narrows a campaign, per country. Several
     * languages are alternatives, as in the condition. Pass them sorted: the cache key
     * is the argument, so ['pt', 'es'] and ['es', 'pt'] would fetch twice.
     */
    getDeviceLanguageSignals: builder.query<DeviceLanguageSignals, string[]>({
      query: (langs) => ({
        method: 'GET',
        url: '/v0/stats/device-language',
        params: { lang: langs.join(',') }
      })
    }),
    getEgeStatsOptions: builder.query<EgeStatsOptions, void>({
      query: () => ({
        method: 'GET',
        url: '/v0/stats/ege/options'
      })
    }),
    getEgeCountryTotals: builder.query<EgeCountryTotal[], EgeStatsFilters>({
      query: (filters) => ({
        method: 'GET',
        url: '/v0/stats/ege/countries',
        params: egeStatsParams(filters)
      })
    }),
    /**
     * The hour-of-day profile. Record dates only -- the service rejects `delivery`,
     * so the caller must pin the date field before asking.
     */
    getEgeHourly: builder.query<EgeHourlyPoint[], EgeStatsFilters>({
      query: (filters) => ({
        url: '/v0/stats/ege/hourly',
        params: egeStatsParams(filters)
      })
    }),
    /**
     * The full (date x hour) grid for the heatmap. Record dates only.
     *
     * One row per (day, hour, source): ~1,440 for the default 30-day range, ~35,000 for
     * the whole archive. Only fetched while the heatmap tab is open.
     */
    getEgeHourlyGrid: builder.query<EgeHourlyCell[], EgeStatsFilters>({
      query: (filters) => ({
        url: '/v0/stats/ege/hourly-grid',
        params: egeStatsParams(filters)
      })
    }),
    /**
     * TRUE unique users per calendar month -- the figure the daily table cannot produce.
     *
     * Its own request rather than a field on `getEgeCountryTotals`, because it comes from
     * a different table at a different grain: that one averages days, this one reports
     * whole months. One row per month overlapping the range, tiny whatever the window.
     */
    getEgeMonthlyUsers: builder.query<EgeMonthlyUsers[], EgeStatsFilters>({
      query: (filters) => ({
        url: '/v0/stats/ege/monthly-users',
        params: egeStatsParams(filters)
      })
    }),
    /**
     * The serving tables at their own grain, for the detail table. Capped server side;
     * the response says whether it was cut short.
     */
    getEgeDetail: builder.query<EgeDetailPage, EgeStatsFilters>({
      query: (filters) => ({
        url: '/v0/stats/ege/detail',
        params: egeStatsParams(filters)
      })
    }),
    getEgeTimeseries: builder.query<EgeTimeseriesPoint[], EgeStatsFilters>({
      query: (filters) => ({
        method: 'GET',
        url: '/v0/stats/ege/timeseries',
        params: egeStatsParams(filters)
      })
    }),
    downloadTackleboxCsv: builder.mutation<Blob, string>({
      query: (jobId) => ({
        method: 'GET',
        url: `v0/tacklebox/download/csv/${jobId}`,
        responseHandler: (response) => response.blob()
      })
    }),
    setFavoriteCampaign: builder.mutation<void, string>({
      invalidatesTags: ['campaigns'],
      query: (campaign_id) => ({
        method: 'PUT',
        url: `/v0/campaign/${campaign_id}/favorite`
      })
    }),
    setUnfavoriteCampaign: builder.mutation<void, string>({
      invalidatesTags: ['campaigns'],
      query: (campaign_id) => ({
        method: 'PUT',
        url: `/v0/campaign/${campaign_id}/unfavorite`
      })
    }),
    getGeonames: builder.query<GeonamesResult[], { latitude: number; longitude: number }>({
      query: ({ latitude, longitude }) => ({
        method: 'GET',
        url: `/v0/meta/geonames/${latitude}/${longitude}`
      })
    }),
    getSavedViews: builder.query<SavedView[], void>({
      providesTags: ['saved_views'],
      query: () => ({
        method: 'GET',
        url: `/v0/views/saved`
      })
    }),
    getSavedView: builder.query<SavedView, string>({
      query: (view_id) => ({
        method: 'GET',
        url: `/v0/views/saved/${view_id}`
      })
    }),
    saveView: builder.mutation<void, View>({
      invalidatesTags: ['saved_views'],
      query: (body) => ({
        method: 'POST',
        url: '/v0/views/save',
        body
      })
    }),
    shareView: builder.mutation<void, { view_id: string; message: string; user: string }>({
      query: ({ view_id, message, user }) => ({
        method: 'POST',
        url: '/v0/views/share',
        body: {
          message,
          user,
          view_id
        }
      })
    }),
    getPolygonIcon: builder.query<string, string>({
      query: (polygon_id) => ({
        url: `/v0/location/thumbnail/${polygon_id}`,
        method: 'GET',
        headers: {
          Accept: 'image/svg+xml'
        },
        responseHandler: (response) => response.text() // treat response as plain text
      })
    }),
    getWifiHex: builder.query<WifiHexResponseRow[], WifiHexRequest>({
      query: ({ bounds, index }) => ({
        method: 'POST',
        url: '/v0/wifi',
        body: {
          bounds,
          index
        }
      })
    }),
    getMOTD: builder.query<{ message: string; level: string; displayType?: string }, void>({
      query: () => ({
        method: 'GET',
        url: '/v0/meta/motd'
      })
    }),
    checkPresence: builder.query<CheckAdvertiserPresenceResponse, CheckAdvertiserPresenceRequest>({
      query: ({ advertiser_ids }) => ({
        method: 'POST',
        url: '/v0/audience/check-presence',
        body: {
          advertiser_ids
        }
      })
    }),
    getLocationUnitInfo: builder.query<GetLocationUnitsResponse, string>({
      query: (polygon_id) => ({
        method: 'GET',
        url: `/v0/location/unit-info/${polygon_id}`
      }),
      providesTags: ['location_unit_info']
    }),
    createLocationUnitInfo: builder.mutation<
      GetLocationUnitsResponse,
      { polygon_id: string; unitNames: string; unitIds: string; sourceLinks: string; description: string; parentUnit: string }
    >({
      query: ({ polygon_id, unitNames, unitIds, sourceLinks, description, parentUnit }) => ({
        method: 'POST',
        url: `/v0/location/unit-info/${polygon_id}`,
        body: {
          unit_names: unitNames
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0),
          unit_ids: unitIds
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0),
          description: description || '',
          parent_unit: parentUnit || '',
          source_links: sourceLinks
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
        }
      }),
      invalidatesTags: ['location_unit_info']
    }),
    updateLocationUnitInfo: builder.mutation<
      GetLocationUnitsResponse,
      { polygon_id: string; unitNames: string; unitIds: string; sourceLinks: string; description: string; parentUnit: string }
    >({
      query: ({ polygon_id, unitNames, unitIds, sourceLinks, description, parentUnit }) => ({
        method: 'PUT',
        url: `/v0/location/unit-info/${polygon_id}`,
        body: {
          unit_names: unitNames
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0),
          unit_ids: unitIds
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0),
          description: description || '',
          parent_unit: parentUnit || '',
          source_links: sourceLinks
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
        }
      }),
      invalidatesTags: ['location_unit_info']
    })
  })
});

export const {
  useGetMOTDQuery,
  useGetWifiHexQuery,
  useShareViewMutation,
  useSaveViewMutation,
  useGetSavedViewsQuery,
  useGetGeonamesQuery,
  useLazyGetSavedViewQuery,
  useUpdateUserRoleForCampaignMutation,
  useRemoveUserFromCampaignMutation,
  useGetCampaignShareSuggestionsQuery,
  useSetFavoriteCampaignMutation,
  useSetUnfavoriteCampaignMutation,
  useUndeleteCampaignMutation,
  useLazyGetAudienceRelationsQuery,
  useGetCampaignTimelineQuery,
  useLazyGetAudienceQuery,
  useLazyGetLocationsQuery,
  useGetContextualSearchResultsQuery,
  useGetGADMCountriesQuery,
  useGetLocalesQuery,
  useGetUserQuery,
  useUpdateSavedStateMutation,
  useDeleteSavedStateMutation,
  useGetSavedStateQuery,
  useGetSavedStatesQuery,
  useSaveStateMutation,
  useGetLocationNotesQuery,
  useGetLocationNetworkQuery,
  useSaveLocationNoteMutation,
  useDeleteLocationNoteMutation,
  useGetEntityNotesQuery,
  useSaveEntityNoteMutation,
  useDeleteEntityNoteMutation,
  useDeleteNotificationMutation,
  useDeleteAllNotificationsMutation,
  useGetLocationsQuery,
  useSeenNotificationMutation,
  useSeenAllNotificationsMutation,
  useGetNotificationsQuery,
  useGetLocationBookmarksQuery,
  useSaveLocationBookmarkMutation,
  useDeleteLocationBookmarkMutation,
  useGetLocationVisitorsDailyQuery,
  useGetAudienceQuery,
  useGetEntityFlightStatusQuery,
  useGetEntityFlightSummariesQuery,
  useGetEntityFlightAircraftSummariesQuery,
  useGetEntityFlightPassengersQuery,
  useGetEntityVesselStatusQuery,
  useGetEntityVesselSummariesQuery,
  useLazyGetEntityVesselJourneysQuery,
  useLazyGetEntityFlightJourneysQuery,
  useGetAudienceBookmarksQuery,
  useSaveAudienceBookmarkMutation,
  useDeleteAudienceBookmarkMutation,
  useGetEntityDwellForLocationsQuery,
  useGetEntityScoresQuery,
  useGetBoundsForEntitiesMutation,
  useTitoRequestMutation,
  useGetTackleboxHistoryQuery,
  useDeleteTackleboxHistoryMutation,
  useRenameTackleboxJobMutation,
  useGetTackleboxJobSharesQuery,
  useShareTackleboxJobMutation,
  useUnshareTackleboxJobMutation,
  useLazyGetTackleboxJobQuery,
  useGetEntityMutation,
  useGetLocationMutation,
  useCreateCampaignMutation,
  useGetCampaignsQuery,
  useToggleCampaignEnabledMutation,
  useRenameCampaignMutation,
  useDeleteCampaignMutation,
  useGetCampaignQuery,
  useLazyGetCampaignQuery,
  useGetTouchstoneStatsQuery,
  useGetDeviceLanguagesQuery,
  useGetDeviceLanguageSignalsQuery,
  useGetEgeStatsOptionsQuery,
  useGetEgeCountryTotalsQuery,
  useGetEgeTimeseriesQuery,
  useGetEgeHourlyQuery,
  useGetEgeHourlyGridQuery,
  useGetEgeMonthlyUsersQuery,
  useGetEgeDetailQuery,
  useDownloadTackleboxCsvMutation,
  useGetEntityDateRangeQuery,
  useGetCampaignEntityTimelineQuery,
  useGetUserSuggestionsQuery,
  useAddUsersToCampaignMutation,
  useGetUsersForCampaignQuery,
  useApproveUserForCampaignMutation,
  useGetPolygonIconQuery,
  useGetLocationUnitInfoQuery,
  useCreateLocationUnitInfoMutation,
  useUpdateLocationUnitInfoMutation,
  useSaveMetricMutation,
  useSubmitAuthErrorMutation,
  useLazyCheckPresenceQuery
} = service;
